import { Injectable } from '@angular/core'
import { FormGroup } from '@angular/forms'
import {
  CertificateApiResponse,
  FinalProvisionsApiData,
  HullSparesCoverProvisionsApiData,
  LiabilityCoverProvisionsApiData
} from '@features/certificate/models/certificate-general-info/certificate-general-info.model'
import { CertificateMetaDataForm } from '@features/certificate/models/certificate-meta-data/certificate-meta-data.model'
import { CertificateDetailsForm } from '@features/certificate/models/certificate-details/certificate-details.model'
import { CertificateContractDetailsForm } from '@features/certificate/models/certificate-contract-details/certificate-contract-details.model'
import { HullSparesCoverProvisionsForm } from '@features/certificate/models/hull-spares-cover-provisions/hull-spares-cover-provisions.model'
import { LiabilityCoverProvisionsForm } from '@features/certificate/models/liability-cover-provisions/liability-cover-provisions.model'
import { FinalProvisionsForm } from '@features/certificate/models/final-provisions/final-provisions.model'
import { CertCertificateHolderApiResponse } from '@features/certificate-holder/models/certificate-holder.model'
import { CertificateDocumentsApiResponse } from '@features/certificate/models/certificate-documents/certificate-documents.model'
import { PolicyForm } from '@features/insured/models/policy/policy.model'
import { PeriodForm } from '@features/insured/models/period/period.model'
import { hasActualValue } from '@core/utils/form-utils'
import {
  AUTHORITIES_SPECIAL_FORMAT,
  CERTIFICATE_AUTHOR,
  CERTIFICATE_STATUS,
  DOCUMENT_STATUS,
  ENGINE_GROSSUP_CLAUSE,
  enumKeyFromRaw,
  EU785_COVER_PACKAGE,
  EXCLUSION_LIABILITY,
  OBJECT_TYPE,
  SUBJECT_TO_CERTIFICATION
} from '@features/certificate/models/certificate-select-options.model'

@Injectable({
  providedIn: 'root'
})
export class CertificateMapperService {
  public buildPayload(
    uuid: string | null,
    insuredUuid: string | null,
    forms: {
      metaData: FormGroup<CertificateMetaDataForm>;
      details: FormGroup<CertificateDetailsForm>;
      contractDetails: FormGroup<CertificateContractDetailsForm>;
      hull: FormGroup<HullSparesCoverProvisionsForm>;
      liability: FormGroup<LiabilityCoverProvisionsForm>;
      finalProv: FormGroup<FinalProvisionsForm>;
      policy: FormGroup<PolicyForm>;
      period: FormGroup<PeriodForm>;
    },
    lists: {
      holders: CertCertificateHolderApiResponse[];
      acr: CertCertificateHolderApiResponse[];
      documents: CertificateDocumentsApiResponse[];
    }
  ): CertificateApiResponse {
    const metaData = forms.metaData.getRawValue()
    const details = forms.details.getRawValue()
    const contractDetails = forms.contractDetails.getRawValue()
    const hull = forms.hull.getRawValue()
    const liability = forms.liability.getRawValue()
    const finalProv = forms.finalProv.getRawValue()

    const hullPayload = this.buildHullSparesCoverProvisionsPayload(hull)
    const liabilityPayload = this.buildLiabilityCoverProvisionsPayload(liability)
    const finalProvPayload = this.buildFinalProvisionsPayload(finalProv)

    return {
      uuid: uuid,
      ...this.buildMetaDataPayload(metaData),
      ...this.buildDetailsPayload(details),
      ...this.buildContractDetailsPayload(contractDetails),
      policy: this.buildPolicy(forms.policy),
      period: this.buildPeriod(forms.period),
      certificateHolders: lists.holders,
      additionalRecipients: lists.acr,
      documents: lists.documents,
      member: this.buildMember(insuredUuid, forms.metaData),
      liabilityCoverCslOnCertificate: liabilityPayload?.cslOnCertificate,
      liabilityCoverAvn52eTotalLimit: liabilityPayload?.avn52eTotalLimit,
      liabilityCoverPersonalInjuryAmount: liabilityPayload?.personalInjuryAmount,
      liabilityCoverAvn59amount: liabilityPayload?.avn59amount,
      liabilityCoverAvn52ePrimaryLimit: liabilityPayload?.avn52ePrimaryLimit,
      liabilityCoverPersonalInjury: liabilityPayload?.personalInjury,
      liabilityCoverAdditionalFreetextLiabilityCheckbox: liabilityPayload?.additionalFreetextLiabilityCheckbox,
      liabilityCoverAdditionalFreetextLiability: liabilityPayload?.additionalFreetextLiability,
      liabilityCoverAdditionalInsured: liabilityPayload?.additionalInsured,
      liabilityCoverSeverabilityOfInterest: liabilityPayload?.severabilityOfInterest,
      liabilityCoverPrimaryClause: liabilityPayload?.primaryClause,
      liabilityCoverCoverNoticeOfCancellationFreetext: liabilityPayload?.coverNoticeOfCancellationFreetext,
      liabilityCoverAdditionalFreetextCheckbox: liabilityPayload?.additionalFreetextCheckbox,
      liabilityCoverAdditionalFreetext: liabilityPayload?.additionalFreetext,
      finalProvisionExclusionLiability: finalProvPayload?.exclusionLiability,
      finalProvisionExclusionNegligenceFreetextCheckbox: finalProvPayload?.exclusionNegligenceFreetextCheckbox,
      finalProvisionExclusionNegligenceFreetext: finalProvPayload?.exclusionNegligenceFreetext,
      finalProvisionFreetextCheckbox: finalProvPayload?.freetextCheckbox,
      finalProvisionFreetext: finalProvPayload?.freetext,
      finalProvisionCutThroughClause: finalProvPayload?.cutThroughClause,
      finalProvisionInsuredSplit: finalProvPayload?.insuredSplit,
      specialFormat: details.authoritiesSpecialFormat ? enumKeyFromRaw(AUTHORITIES_SPECIAL_FORMAT, details.authoritiesSpecialFormat) : 'NONE',
      linkedCertificate: details.linkedCertificates ?? null,
      aircraftType: [hullPayload?.aircraftType?.[0] as string],
      aircraftTypeForCertificate: hullPayload?.aircraftForTypeCertificate ?? null,
      aircraftSerialNumber: hullPayload?.aircraftSerialNumber ?? null,
      aircraftRegistration: hullPayload?.aircraftRegistration ?? null,
      equipmentType: hullPayload?.equipmentType ?? null,
      equipmentSerialNumber: hullPayload?.equipmentSerialNumber ?? null,
      equipmentTypeFreetextCheckbox: hullPayload?.equipmentTypeFreetextCheckbox ?? false,
      equipmentTypeFreetext: hullPayload?.equipmentTypeFreetext ?? null,
      hullAllRiskDeductible: hullPayload?.enableHullAllRiskDeductible ?? false,
      hullAllRiskDeductibleAmount: hullPayload?.hullAllRiskDeductible,
      fleetOfAircraft: hullPayload?.fleetOfAircraft ?? false,
      fleetListAsAttachment: hullPayload?.fleetListAsAttachment ?? false,
      fleetListFormat: 'DEFAULT',
      filterByAircraftType: hullPayload?.filterByAircraftType ?? false,
      aircraftTypes: hullPayload?.aircraftTypeFilter ?? [],
      includePlannedAircraft: hullPayload?.includePlannedAircraft ?? false,
      hullInsurance: hullPayload?.hullInsurance ?? null,
      hullWarInsurance: hullPayload?.hullWarInsurance ?? null,
      sparesAllRiskInsurance: hullPayload?.sparesAllRiskInsurance ?? null,
      hullCoverAndProvisionsWaiverOfSubrogation: hullPayload?.waiverOfSubrogation ?? null,
      noticeOfCancellation: hullPayload?.noticeOfCancellation ?? null,
      contractNoticeOfCancellation: hullPayload?.noticeOfCancellation ?? null,
      hullCoverAndProvisionsNoLiabilityForPremiums: hullPayload?.noLiabilityForPremiums ?? null,
      hullCoverAndProvisionsBreachOfWarranty: hullPayload?.breachOfWarranty ?? null,
      additionalInsured: hullPayload?.additionalInsured ?? false,
      contractAdditionalInsured: hullPayload?.additionalInsured ?? false,
      freetextCheckbox: hullPayload?.freetextCheckbox ?? false,
      freetext: hullPayload?.freetext ?? null,
      groundingLiabilityAmount: liabilityPayload?.groundingLiability,
      passengerBaggageDeductibleAmount: liabilityPayload?.passengerBaggageDeductibleAmount,
      cargoAndMailDeductibleAmount: liabilityPayload?.cargoAndMailDeductibleAmount,
      standardCoverPackageAirline: liabilityPayload?.standardCoverPackageAirline ?? false,
      standardCoverPackage: liabilityPayload?.standardCoverPackage ?? false,
      standardCoverPackageMro: liabilityPayload?.standardCoverPackageMro ?? false,
      eu785CoverPackage: liabilityPayload?.eu785CoverPackage ?? 'NONE',
      sdrAmount: liabilityPayload?.sdrAmount,
      aircraftThirdPartyLiability: liabilityPayload?.aircraftThirdPartyLiability ?? false,
      passengerLiability: liabilityPayload?.passengerLiability ?? false,
      cargoAndMailLiability: liabilityPayload?.cargoAndMailLiability ?? false,
      hangarkeepersLiability: liabilityPayload?.hangarkeepersLiability ?? false,
      productsLiability: liabilityPayload?.productsLiability ?? false,
      productsCompletedOperationsLiability: liabilityPayload?.productsCompletedOperationsLiability ?? false,
      motorLiabilityNonRegistered: liabilityPayload?.motorLiabilityNonRegistered ?? false,
      motorLiabilityRegistered: liabilityPayload?.motorLiabilityRegistered ?? false,
      aviationPremiseLiability: liabilityPayload?.aviationPremiseLiability ?? false,
      aviationGeneralThirdPartyLiability: liabilityPayload?.aviationGeneralThirdPartyLiability ?? false,
      bhvPrimaryAmount: liabilityPayload?.bhvPrimaryAmount,
      bhvPrimaryFreetextCheckbox: liabilityPayload?.bhvPrimaryFreetextCheckbox ?? false,
      bhvPrimaryFreetext: liabilityPayload?.bhvPrimaryFreetext ?? null,
      bhvExcessOnly: liabilityPayload?.bhvExcessOnly ?? false,
      liabilityCoverAndProvisionsBreachOfWarranty: liabilityPayload?.breachOfWarranty ?? null,
      contingencyCodeshare: liabilityPayload?.contingencyCodeshare ?? false,
      liabilityCoverAndProvisionsVaiverOfSubrogation: liabilityPayload?.waiverOfSubrogation ?? false,
      liabilityCoverAndProvisionsNoLiabilityForPremiums: liabilityPayload?.noLiabilityForPremiums ?? null,
      airBrokerIncluded: finalProvPayload?.airBrokerIncluded ?? false,
      airBrokerName: finalProvPayload?.airBrokerName ?? null,
      boingDefinition: finalProvPayload?.boeingDefinition ?? false,
      exclusionManufacturerRepairer: finalProvPayload?.exclusionManufacturerRepairer ?? false,
      exclusionWorkmensCompensation: finalProvPayload?.exclusionWorkmensCompensation ?? false,
      brokerLetter: finalProvPayload?.brokerLetter ?? false,
      cslOnCertificate: liabilityPayload?.cslOnCertificate,
      avn52eTotalLimit: liabilityPayload?.avn52eTotalLimit,
      personalInjuryAmount: liabilityPayload?.personalInjuryAmount,
      avn59amount: liabilityPayload?.avn59amount,
      avn52ePrimaryLimit: liabilityPayload?.avn52ePrimaryLimit,
      personalInjury: liabilityPayload?.personalInjury,
      additionalFreetextLiabilityCheckbox: liabilityPayload?.additionalFreetextLiabilityCheckbox,
      additionalFreetextLiability: liabilityPayload?.additionalFreetextLiability,
      severabilityOfInterest: liabilityPayload?.severabilityOfInterest,
      primaryClause: liabilityPayload?.primaryClause,
      coverNoticeOfCancellationFreetext: liabilityPayload?.coverNoticeOfCancellationFreetext,
      additionalFreetextCheckbox: liabilityPayload?.additionalFreetextCheckbox,
      additionalFreetext: liabilityPayload?.additionalFreetext,
      exclusionLiability: finalProvPayload?.exclusionLiability,
      exclusionNegligenceFreetextCheckbox: finalProvPayload?.exclusionNegligenceFreetextCheckbox,
      exclusionNegligenceFreetext: finalProvPayload?.exclusionNegligenceFreetext,
      cutThroughClause: finalProvPayload?.cutThroughClause,
      insuredSplit: finalProvPayload?.insuredSplit,
      liabilityLimitsPerAircraftType: liabilityPayload?.liabilityLimitsPerAircraftType ?? false,
      liabilityLimitAmountAircraft: liabilityPayload?.liabilityLimitAmountAircraft ?? [],
      liabilityAircraftType: liabilityPayload?.liabilityAircraftType ?? [],
      liabilityAvn52eLimitsFleetList: liabilityPayload?.liabilityAvn52eLimitsFleetList ?? false,
      equipmentTypeForCertificate: hullPayload?.equipmentTypeForCertificate ?? null,
      objectType: enumKeyFromRaw(OBJECT_TYPE, hull.objectType),
      engineGrossUpClause: enumKeyFromRaw(ENGINE_GROSSUP_CLAUSE, hull.engineGrossupClause),
      engineGrossUpClauseFreetextCheckbox: hullPayload?.engineGrossUpClauseFreetextCheckbox,
      engineGrossUpClauseFreetext: hullPayload?.engineGrossUpClauseFreetext,
      contractualLiability: liabilityPayload?.contractualLiability ?? false,
      remainingDeductible: hullPayload?.remainingDeductible,
      leaseValueTba: hullPayload?.leaseValueTba,
      leaseValues: hullPayload?.leaseValues,
      titledEngineValues: hullPayload?.titledEngines,
      sublimitLiquidLiabilityAmount: liabilityPayload?.sublimitLiquidLiabilityAmount,
      deductibleAllLossesAmount: liabilityPayload?.deductibleAllLossesAmount,
      liabilityAvn52eVariousLimits: liabilityPayload?.liabilityAvn52eVariousLimits
    }
  }

  private buildPolicy(policyForm: FormGroup<PolicyForm>): CertificateApiResponse['policy'] {
    const policy = policyForm.getRawValue()
    return hasActualValue(policy.uuid) ? {
      uuid: policy.uuid!,
      coverageInsuranceType: policy.coverageType?.localValue ?? null,
      policyStatus: policy.status ?? null
    } : null
  }

  private buildPeriod(periodForm: FormGroup<PeriodForm>): CertificateApiResponse['period'] {
    const period = periodForm.getRawValue()
    return hasActualValue(period.uuid) ? {
      uuid: period.uuid!,
      name: period.name ?? null,
      periodFrom: period.periodFrom ?? null,
      perdioTo: period.periodTo ?? null
    } : null
  }

  private buildMember(insuredUuid: string | null, metaDataForm: FormGroup<CertificateMetaDataForm>): CertificateApiResponse['member'] {
    const member = metaDataForm.getRawValue().member
    if (insuredUuid !== null) {
      return { uuid: insuredUuid, name: member?.name ?? null, coverageType: member?.coverageType ?? null }
    }
    const selectedMemberId = member?.uuid
    return hasActualValue(selectedMemberId)
      ? { uuid: selectedMemberId, name: member?.name ?? null, coverageType: member?.coverageType ?? null }
      : null
  }

  private buildMetaDataPayload(metaData: ReturnType<FormGroup<CertificateMetaDataForm>['getRawValue']>):
    Pick<CertificateApiResponse,
      'internalComment' |
      'externalComment' |
      'member' |
      'certificateCategory' |
      'certificateChangedBy' |
      'certificatePublishedBy'
    > {
    return {
      internalComment: metaData.internalComment ?? null,
      externalComment: metaData.externalComment ?? null,
      member: null,
      certificateCategory: metaData.certificateCategory ?? null,
      certificatePublishedBy: metaData.certificatePublishedBy,
      certificateChangedBy: metaData.certificateChangedBy
    }
  }

  private buildDetailsPayload(details: ReturnType<FormGroup<CertificateDetailsForm>['getRawValue']>):
    Pick<CertificateApiResponse,
      'certificateNumber' |
      'certificateIdValue' |
      'status' |
      'certificateStatus' |
      'certificateTitle' |
      'effectiveDate' |
      'subjectToCertification' |
      'subjectToCertificationFreetext' |
      'notToBeRenewed' |
      'authoritiesSpecialFormat' |
      'certificateAuthor' |
      'documentStatus' |
      'importedDoc' |
      'linkedCertificates' |
      'associatedCompanies'
    > {
    return {
      certificateNumber: details.certificateIdValue?.certificateNumber ?? null,
      certificateIdValue: details.certificateIdValue ?? null,
      status: enumKeyFromRaw(CERTIFICATE_STATUS, details.status) ?? 'DRAFT',
      certificateStatus: enumKeyFromRaw(CERTIFICATE_STATUS, details.status) ?? 'DRAFT',
      certificateTitle: details.title ?? null,
      effectiveDate: details.effectiveDate instanceof Date ? details.effectiveDate.toISOString() : (details.effectiveDate ?? null),
      subjectToCertification: enumKeyFromRaw(SUBJECT_TO_CERTIFICATION, details.subjectToCertification),
      subjectToCertificationFreetext: this.buildCoverSection(details.subjectToCertificationFreetext),
      notToBeRenewed: details.notToBeRenewed ?? false,
      authoritiesSpecialFormat: enumKeyFromRaw(AUTHORITIES_SPECIAL_FORMAT, details.authoritiesSpecialFormat) ?? 'NONE',
      certificateAuthor: enumKeyFromRaw(CERTIFICATE_AUTHOR, details.certificateAuthor) ?? 'Albatros',
      documentStatus: enumKeyFromRaw(DOCUMENT_STATUS, details.documentStatus) ?? 'NONE',
      importedDoc: details.importedDoc ?? false,
      linkedCertificates: details.linkedCertificates ?? null,
      associatedCompanies: details.associatedCompanies ?? false,
    }
  }

  private buildContractDetailsPayload(contractDetails: ReturnType<FormGroup<CertificateContractDetailsForm>['getRawValue']>):
    Pick<CertificateApiResponse,
      'furtherInsured' |
      'contractParties' |
      'contractPartiesEuat' |
      'contracts' |
      'avn67b' |
      'avn67c' |
      'avn67cDesignatedContractParty'
    > {
    return {
      furtherInsured: (contractDetails.furtherInsured ?? []).map(item => ({
        memberId: item.memberId,
        company: item.company,
        policyId: item.policyId ?? null
      })),
      contractParties: contractDetails.contractParties ?? null,
      contractPartiesEuat: contractDetails.contractPartiesEuat ?? [],
      contracts: contractDetails.contracts ?? null,
      avn67b: contractDetails.avn67B ?? null,
      avn67c: contractDetails.avn67C ?? null,
      avn67cDesignatedContractParty: contractDetails.designatedContractParty ?? null,
    }
  }

  private buildHullSparesCoverProvisionsPayload(hull: ReturnType<FormGroup<HullSparesCoverProvisionsForm>['getRawValue']>): HullSparesCoverProvisionsApiData {
    const leaseValues = (hull.leaseValues ?? []).map(entry => ({
      amount: entry.amount ?? 0, currency: entry.currency ?? null
    }))

    const titledEngines = (hull.titledEngines ?? []).map(entry => ({
      type: entry.type ?? null, serialNumber: entry.serialNumber ?? null
    }))

    return {
      uuid: null,
      certificateId: null,
      objectType: enumKeyFromRaw(OBJECT_TYPE, hull.objectType),
      equipmentTypeFreetextCheckbox: hull.equipmentTypeFreetextCheckbox ?? false,
      equipmentTypeFreetext: hull.equipmentTypeFreetext ?? null,
      aircraftType: hull.aircraftType ?? [],
      aircraftTypeFilter: hull.aircraftTypeFilter ?? [],
      aircraftForTypeCertificate: hull.aircraftTypeForCertificates ?? null,
      aircraftSerialNumber: hull.aircraftSerialNumber ?? null,
      aircraftRegistration: hull.aircraftRegistration ?? null,
      titledEngines: titledEngines,
      engineGrossUpClause: enumKeyFromRaw(ENGINE_GROSSUP_CLAUSE, hull.engineGrossupClause),
      equipmentType: hull.equipmentType ?? null,
      equipmentTypeForCertificate: hull.equipmentTypeForCertificate ?? null,
      equipmentSerialNumber: hull.equipmentSerialNumber ?? null,
      leaseValues: leaseValues,
      leaseValueTba: hull.leaseValueTba ?? false,
      enableHullAllRiskDeductible: hull.hullAllRiskDeductible ?? false,
      hullAllRiskDeductible: this.buildAmountOrNull(hull.hullAllRiskDeductibleAmount),
      remainingDeductible: this.buildAmountOrNull(hull.remainingDeductibleAmount),
      fleetOfAircraft: hull.fleetOfAircraft ?? false,
      fleetListAsAttachment: hull.addFleetListAsAttachment ?? false,
      filterByAircraftType: hull.filterByAircraftType ?? false,
      includePlannedAircraft: hull.includePlannedAircraft ?? false,
      hullInsurance: this.buildCoverSection(hull.hullInsurance),
      hullWarInsurance: this.buildCoverSection(hull.hullWarInsurance),
      sparesAllRiskInsurance: {
        ...this.buildCoverSection(hull.sparesAllRiskInsurance),
        includeEngineTestRunOnCertificate: hull.includeEngineTestRunOnCertificate ?? false,
        includeSparesDeductibleOnCertificate: hull.includeSparesDeductibleOnCertificate ?? false
      },
      waiverOfSubrogation: this.buildCoverSection(hull.waiverOfSubrogation),
      noticeOfCancellation: this.buildNoticeOfCancellationSection(hull.noticeOfCancellationInclWar, hull.noticeOfCancellationNoWar),
      noLiabilityForPremiums: this.buildCoverSection(hull.noLiabilityForPremiums),
      breachOfWarranty: this.buildCoverSection(hull.breachOfWarranty),
      additionalInsured: hull.additionalInsured?.checkbox ?? false,
      freetextCheckbox: hull.additionalInsured?.freetextCheckbox ?? null,
      freetext: hull.additionalInsured?.freetext ?? null,
      engineGrossUpClauseFreetext: hull.engineGrossUpClauseFreetext ?? null,
      engineGrossUpClauseFreetextCheckbox: hull.engineGrossUpClauseFreetextCheckbox ?? null,
    }
  }

  private buildLiabilityCoverProvisionsPayload(
    liability: ReturnType<FormGroup<LiabilityCoverProvisionsForm>['getRawValue']>
  ): LiabilityCoverProvisionsApiData {

    const rawLimits = liability.liabilityLimitAmountAircraft ?? []
    const rawTypes = liability.liabilityAircraftType ?? []
    const usedRows = Array.from({ length: Math.max(rawLimits.length, rawTypes.length) }, (_, index) => ({
      limit: rawLimits[index] ?? null,
      type: rawTypes[index] ?? null
    })).filter(row => hasActualValue(row.limit?.amount) || hasActualValue(row.type))
    const liabilityLimitAmountAircraft = usedRows.map(row => ({
      amount: row.limit?.amount ?? 0, currency: row.limit?.currency ?? null
    }))
    const liabilityAircraftType = usedRows.map(row => row.type ?? '')

    return {
      certificateId: null,
      uuid: null,
      cslOnCertificate: this.buildAmountOrNull(liability.cslOnCertificateAmount),
      avn52eTotalLimit: this.buildAmountOrNull(liability.avn52eTotalLimitAmount),
      groundingLiability: this.buildAmountOrNull(liability.groundingLiabilityAmount),
      personalInjuryAmount: this.buildAmountOrNull(liability.personalInjuryAmount),
      avn59amount: this.buildAmountOrNull(liability.avn59Amount),
      passengerBaggageDeductibleAmount: this.buildAmountOrNull(liability.passengerBaggageDeductibleAmount),
      cargoAndMailDeductibleAmount: this.buildAmountOrNull(liability.cargoAndMailDeductibleAmount),
      sublimitLiquidLiabilityAmount: this.buildAmountOrNull(liability.sublimitLiquidLiabilityAmount),
      deductibleAllLossesAmount: this.buildAmountOrNull(liability.deductibleAllLossesAmount),
      standardCoverPackage: liability.standardCoverPackage ?? false,
      standardCoverPackageAirline: liability.standardCoverPackageAirline ?? false,
      standardCoverPackageMro: liability.standardCoverPackageMro ?? false,
      eu785CoverPackage: enumKeyFromRaw(EU785_COVER_PACKAGE, liability.eu785CoverPackage),
      sdrAmount: this.buildAmountOrNull(liability.sdrAmount),
      aircraftThirdPartyLiability: liability.aircraftThirdPartyLiability ?? false,
      avn52ePrimaryLimit: liability.avn52ePrimaryLimit ?? false,
      passengerLiability: liability.passengerLiability ?? false,
      personalInjury: liability.personalInjury ?? false,
      cargoAndMailLiability: liability.cargoAndMailLiability ?? false,
      hangarkeepersLiability: liability.hangarkeepersLiability ?? false,
      productsLiability: liability.productsLiability ?? false,
      productsCompletedOperationsLiability: liability.productsCompletedOperationsLiability ?? false,
      motorLiabilityNonRegistered: liability.motorLiabilityNonRegistered ?? false,
      motorLiabilityRegistered: liability.motorLiabilityRegistered ?? false,
      aviationPremiseLiability: liability.aviationPremiseLiability ?? false,
      aviationGeneralThirdPartyLiability: liability.aviationGeneralThirdPartyLiability ?? false,
      contractualLiability: liability.contractualLiability ?? false,
      additionalFreetextLiabilityCheckbox: liability.additionalFreetextLiability?.freetextCheckbox ?? false,
      additionalFreetextLiability: liability.additionalFreetextLiability?.freetext ?? null,
      bhvPrimaryAmount: this.buildAmountOrNull(liability.bhvPrimaryAmount),
      bhvPrimaryFreetextCheckbox: liability.bhvPrimaryFreetext?.freetextCheckbox ?? false,
      bhvPrimaryFreetext: liability.bhvPrimaryFreetext?.freetext ?? null,
      bhvExcessOnly: liability.bhvExcessOnly ?? false,
      additionalInsured: this.buildCoverSectionOrNull(liability.additionalInsured),
      severabilityOfInterest: this.buildCoverSectionOrNull(liability.severabilityOfInterest),
      breachOfWarranty: this.buildCoverSectionOrNull(liability.breachOfWarranty),
      contingencyCodeshare: liability.contingencyCodeshare ?? false,
      waiverOfSubrogation: liability.waiverOfSubrogation ?? false,
      primaryClause: this.buildCoverSectionOrNull(liability.primaryClause),
      noLiabilityForPremiums: this.buildCoverSectionOrNull(liability.noLiabilityForPremiums),
      coverNoticeOfCancellationFreetext: this.buildNoticeOfCancellationSection(liability.noticeOfCancellationInclWar, liability.noticeOfCancellationNoWar),
      additionalFreetextCheckbox: liability.additionalFreetext?.freetextCheckbox ?? false,
      additionalFreetext: liability.additionalFreetext?.freetext ?? null,
      liabilityLimitsPerAircraftType: liability.liabilityLimitsPerAircraftType ?? false,
      liabilityLimitAmountAircraft: liabilityLimitAmountAircraft,
      liabilityAircraftType: liabilityAircraftType,
      liabilityAvn52eLimitsFleetList: liability.liabilityAvn52eLimitsFleetList ?? false,
      liabilityAvn52eVariousLimits: liability.liabilityAvn52eVariousLimits ?? false
    }
  }

  private buildFinalProvisionsPayload(finalProv: ReturnType<FormGroup<FinalProvisionsForm>['getRawValue']>): FinalProvisionsApiData {
    return {
      uuid: null,
      certificateId: null,
      exclusionLiability: enumKeyFromRaw(EXCLUSION_LIABILITY, finalProv.exclusionLiability),
      exclusionNegligenceFreetextCheckbox: finalProv.exclNeglWilfulMisconduct?.freetextCheckbox ?? false,
      exclusionNegligenceFreetext: finalProv.exclNeglWilfulMisconduct?.freetext ?? null,
      airBrokerIncluded: finalProv.airBrokerIncluded ?? false,
      airBrokerName: finalProv.airBrokerName ?? null,
      boeingDefinition: finalProv.boeingDefinition ?? false,
      exclusionManufacturerRepairer: finalProv.exclusionManufacturerRepairer ?? false,
      exclusionWorkmensCompensation: finalProv.exclusionWorkmensCompensation ?? false,
      freetextCheckbox: finalProv.freetext?.freetextCheckbox ?? false,
      freetext: finalProv.freetext?.freetext ?? null,
      cutThroughClause: finalProv.includeCutThroughClause ?? false,
      brokerLetter: finalProv.includeBrokerLetterOfUndertaking ?? false,
      insuredSplit: finalProv.includeInsurerSplit ?? false,
    }
  }

  private buildAmountOrNull(value: { amount: number | null; currency: string | null }): { amount: number; currency: string | null } | null {
    return hasActualValue(value.amount) ? { amount: value.amount ?? 0, currency: value.currency ?? null } : null
  }

  private buildCoverSection(
    freetext: { uuid?: string | null; checkbox?: boolean | null; freetextCheckbox?: boolean | null; freetext?: string | null } | null | undefined
  ): {
    uuid: string | null;
    cover: boolean;
    coverFreetextCheckbox: boolean;
    coverFreetext: string | null
  } {
    return {
      uuid: freetext?.uuid ?? null,
      cover: freetext?.checkbox ?? false,
      coverFreetextCheckbox: freetext?.freetextCheckbox ?? false,
      coverFreetext: freetext?.freetext ?? null
    }
  }

  private buildCoverSectionOrNull(freetext: { uuid?: string | null; checkbox?: boolean | null; freetextCheckbox?: boolean | null; freetext?: string | null }): {
    uuid: string | null;
    cover: boolean;
    coverFreetextCheckbox: boolean;
    coverFreetext: string | null
  } | null {
    return hasActualValue(freetext.freetextCheckbox) || hasActualValue(freetext.uuid) ? this.buildCoverSection(freetext) : null
  }

  private buildNoticeOfCancellationSection(
    inclWar: boolean | null | undefined,
    noWar: { uuid?: string | null; checkbox?: boolean | null; freetextCheckbox?: boolean | null; freetext?: string | null } | null | undefined
  ): {
    uuid: string | null;
    noticeOfCancellationInclWar: boolean;
    noticeOfCancellationNoWar: boolean;
    noticeOfCancellationFreetextCheckbox: boolean;
    noticeOfCancellationFreetext: string | null
  } {
    return {
      uuid: noWar?.uuid ?? null,
      noticeOfCancellationInclWar: inclWar ?? false,
      noticeOfCancellationNoWar: noWar?.checkbox ?? false,
      noticeOfCancellationFreetextCheckbox: noWar?.freetextCheckbox ?? false,
      noticeOfCancellationFreetext: noWar?.freetext ?? null
    }
  }
}
